import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ListingsGridService } from 'listings/services/listings-grid.service';
import clone from 'lodash-es/clone';
import { combineLatest, map, of, Subscription, switchMap, tap, throwError } from 'rxjs';
import { Brand } from 'settings/interfaces/brand.interface';
import { SuppliersService } from 'settings/services/suppliers.service';
import { BaseModal } from 'shared/abstract/base-modal';
import { EventBusService, FormDifferService, ToastService, UserApiService } from 'shared/services';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { mutuallyExclusiveRequiredValidator } from 'shared/validators/mutually-exclusive-required-validator';
import { AccessGroup } from 'users/interfaces/access-group.interface';
import { ListingAccessFilter } from 'users/interfaces/listing-access-filter';
import { AccessGroupsService } from 'users/services/access-groups.service';

@Component({
    selector: 'app-edit-access-group',
    templateUrl: './edit-access-group.component.html',
    styleUrls: ['./edit-access-group.component.scss'],
    standalone: false,
})
export class EditAccessGroupComponent extends BaseModal implements OnInit, OnDestroy {
    public data!: any;

    public showListingsFilter = false;
    public accessGroup!: any;
    public accessGroupFilters!: AccessGroup;
    public userIsAdmin!: boolean;
    public showFormError = false;

    public dynamicOptions: { getDataFunction: Function; getDataFunctionParams?: any } = {
        getDataFunction: this.listingsService.getFilteredListingsForAccessGroups.bind(this.listingsService),
    };

    public editAccessGroupForm = new UntypedFormGroup(
        {
            companyName: new UntypedFormControl({ value: null, disabled: true }, [Validators.required]),
            name: new UntypedFormControl(null, [Validators.required, Validators.maxLength(64)]),
            brands: new UntypedFormControl(null),
            countries: new UntypedFormControl(null),
            listings: new UntypedFormControl(null),
        },
        mutuallyExclusiveRequiredValidator([{ controlName: 'brands' }, { controlName: 'countries' }, { controlName: 'listings' }]),
    );

    private subscriptions: { [key: string]: Subscription | undefined } = {};

    constructor(
        private eventBusService: EventBusService,
        private accessGroupService: AccessGroupsService,
        private suppliersGridService: SuppliersService,
        private userApiService: UserApiService,
        private translate: TranslateService,
        private toastService: ToastService,
        private listingsService: ListingsGridService,
        private formDifferService: FormDifferService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.closeModalEvent = this.eventBusService
            .getObservableForEventStream('closeUserModal')
            .subscribe(() => this.closeModalInstance(undefined));
        this.editAccessGroupForm.get('companyName')?.setValue(this.data.supplierId);

        this.subscriptions.getLoginInformation = this.userApiService
            .getLoginInformation()
            .subscribe((userInformation) => (this.userIsAdmin = userInformation.isShiji));

        this.subscriptions.getAccessGroup = this.accessGroupService
            .getListingAccessGroup(this.data.id)
            .pipe(
                map((group: any) => {
                    group.brands = group.listingAccesses?.filter((access: any) => access.accessType === 'BRAND').slice();
                    group.countries = group.listingAccesses?.filter((access: any) => access.accessType === 'COUNTRY').slice();
                    group.listings = group.listingAccesses?.filter((access: any) => access.accessType === 'LISTING').slice();
                    if (group.brands?.length === 0) {
                        group.brands = null;
                    }
                    if (group.countries?.length === 0) {
                        group.countries = null;
                    }
                    if (group.listings?.length === 0) {
                        group.listings = null;
                    } else {
                        this.showListingsFilter = true;
                    }

                    return group;
                }),
                switchMap((group: any) => {
                    //This will be changed soon to become a typeahead with the search done on the BE due to the possible great number of listings
                    const body: ListingAccessFilter = {
                        supplierId: this.data.supplierId,
                        brandIds: group?.brands?.map((brand: any) => brand.accessIdValue),
                        countryIds: group?.countries?.map((country: any) => country.accessIdValue),
                    };

                    this.dynamicOptions = clone(this.dynamicOptions);
                    this.dynamicOptions.getDataFunctionParams = [group.supplierId, group.companyName];

                    return combineLatest([of(group), this.accessGroupService.getListingAccessFilter(body)]);
                }),
            )
            .subscribe(([group, accessGroupFilters]) => {
                this.accessGroupFilters = accessGroupFilters;
                this.accessGroup = group;

                this.subscriptions.brandsChange = this.editAccessGroupForm.get('brands')?.valueChanges?.subscribe((value: any) => {
                    this.getUpdatedFilters(this.data.supplierId, value, this.editAccessGroupForm.value.countries);
                });
                this.subscriptions.countriesChange = this.editAccessGroupForm.get('countries')?.valueChanges?.subscribe((value) => {
                    this.getUpdatedFilters(this.data.supplierId, this.editAccessGroupForm.value.brands, value);
                });

                this.editAccessGroupForm.get('companyName')?.setValue(this.data.supplierId);
                this.editAccessGroupForm.get('name')?.setValue(this.data.name);
                this.editAccessGroupForm.get('brands')?.setValue(
                    this.accessGroup?.brands?.map((brand: any) => brand.accessIdValue),
                    { emitEvent: false },
                );
                this.editAccessGroupForm.get('countries')?.setValue(
                    this.accessGroup?.countries?.map((country: any) => country.accessIdValue),
                    { emitEvent: false },
                );
                this.editAccessGroupForm
                    .get('listings')
                    ?.setValue(this.accessGroup?.listings?.map((listing: any) => listing.accessIdValue));

                this.formDifferService.registerForm(this.editAccessGroupForm);
                this.formDifferService.createDataSnapshot();
            });
    }

    private getUpdatedFilters(supplierId: string, brandIds?: string[] | null, countryIds?: string[] | null) {
        const params: ListingAccessFilter = {
            supplierId: supplierId,
        };

        if (brandIds) {
            params.brandIds = brandIds;
        }

        if (countryIds) {
            params.countryIds = countryIds;
        }

        let selectedBrands: any;
        let selectedCountries: any;

        this.subscriptions.listingAccessFilter = this.accessGroupService
            .getListingAccessFilter(params)
            .pipe(
                tap((filters) => (this.accessGroupFilters = filters)),
                switchMap((filters) => {
                    const brandsControl = this.editAccessGroupForm.get('brands') as UntypedFormControl;
                    const countriesControl = this.editAccessGroupForm.get('countries') as UntypedFormControl;

                    if (brandsControl.value) {
                        selectedBrands = brandsControl.value.filter((brandId: string) =>
                            filters.brands.find((brand: Brand) => brand.id === brandId),
                        );
                        brandsControl.setValue(selectedBrands, { emitEvent: false });
                    }

                    if (countriesControl.value) {
                        selectedCountries = countriesControl.value.filter((countryId: string) =>
                            filters.countries.find((country: any) => country.id.toString() === countryId.toString()),
                        );
                        countriesControl.setValue(selectedCountries, { emitEvent: false });
                    }

                    params.brandIds = brandsControl.value;
                    params.countryIds = countriesControl.value;

                    return this.accessGroupService.getListingAccessFilter(params);
                }),
            )
            .subscribe((filterResults) => {
                this.accessGroupFilters.countries = filterResults.countries;
                this.accessGroupFilters.brands = filterResults.brands;
            });
    }

    public updateAccessGroup() {
        this.editAccessGroupForm.markAllAsTouched();
        ['name'].forEach((controlName) => this.editAccessGroupForm.get(controlName)?.updateValueAndValidity());
        this.showFormError = true;

        if (this.editAccessGroupForm.valid) {
            const body: any = {
                ...this.formDifferService.getDiffWithSnapshot(),
                id: this.data.id,
                listingAccessesInput: [].concat(
                    this.editAccessGroupForm.value.brands?.map((brandId: string) => {
                        return { accessIdValue: brandId, accessType: 'BRAND' };
                    }) ?? [],
                    this.editAccessGroupForm.value.countries?.map((countryId: string) => {
                        return { accessIdValue: countryId, accessType: 'COUNTRY' };
                    }) ?? [],
                    this.editAccessGroupForm.value.listings?.map((listingId: string) => {
                        return { accessIdValue: listingId, accessType: 'LISTING' };
                    }) ?? [],
                ),
            };

            this.subscriptions.updateListingAccessGroup = this.accessGroupService.updateListingAccessGroup(body).subscribe((response) => {
                if (response.statusCode.toString().toLowerCase() === 'conflict') {
                    this.toastService.showToast('users.accessGroups.createEdit.alreadyExists', 'error', {
                        groupName: this.editAccessGroupForm.value.name,
                    });
                    throwError(() => new Error('A group with that name already exists.'));
                } else {
                    this.toastService.showToast('users.accessGroups.createEdit.updateSuccess', 'success', {
                        groupName: this.editAccessGroupForm.value.name,
                    });
                    this.closeModal(true);
                }
            });
        }
    }

    public closeModal(result: boolean): void {
        this.closeModalInstance(result);
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }
}
